import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static revealDelay = 1000

  connect() {
    this.update = this.update.bind(this)
    this.hide = this.hide.bind(this)
    this.scheduleUpdate = this.scheduleUpdate.bind(this)

    this.spotlight = document.createElement("div")
    this.spotlight.className = "demo-spotlight"
    this.spotlight.hidden = true
    this.spotlight.setAttribute("aria-hidden", "true")
    this.shade = document.createElement("div")
    this.shade.className = "demo-spotlight__shade"
    this.spotlight.appendChild(this.shade)
    this.callout = document.createElement("div")
    this.callout.className = "demo-spotlight__callout"
    this.spotlight.appendChild(this.callout)
    this.arrow = document.createElement("span")
    this.arrow.className = "demo-spotlight__arrow"
    this.spotlight.appendChild(this.arrow)
    document.body.appendChild(this.spotlight)

    this.resizeObserver = new ResizeObserver(this.scheduleUpdate)
    window.addEventListener("resize", this.scheduleUpdate)
    window.addEventListener("scroll", this.scheduleUpdate, true)
    document.addEventListener("turbo:frame-load", this.scheduleUpdate)
    document.addEventListener("show.bs.modal", this.hide)
    document.addEventListener("hidden.bs.modal", this.scheduleUpdate)
    this.ready = false
    this.revealTimer = window.setTimeout(() => {
      this.ready = true
      this.scheduleUpdate()
    }, this.constructor.revealDelay)
  }

  disconnect() {
    window.removeEventListener("resize", this.scheduleUpdate)
    window.removeEventListener("scroll", this.scheduleUpdate, true)
    document.removeEventListener("turbo:frame-load", this.scheduleUpdate)
    document.removeEventListener("show.bs.modal", this.hide)
    document.removeEventListener("hidden.bs.modal", this.scheduleUpdate)
    window.cancelAnimationFrame(this.frame)
    window.clearTimeout(this.revealTimer)
    window.clearTimeout(this.dismissTimer)
    this.resizeObserver.disconnect()
    this.spotlight.remove()
  }

  scheduleUpdate() {
    window.cancelAnimationFrame(this.frame)
    this.frame = window.requestAnimationFrame(this.update)
  }

  hide() {
    this.spotlight.style.visibility = "hidden"
  }

  update() {
    if (!this.ready) return
    if (document.body.classList.contains("modal-open")) return this.hide()

    const candidates = this.element.querySelectorAll("[data-demo-spotlight-focus], .demo-tour__highlight")
    const visible = Array.from(candidates).filter((element) => element.getClientRects().length > 0)
    const target = visible.find((element) => element.hasAttribute("data-demo-spotlight-focus")) || visible[0]
    if (!target) return this.hide()

    if (this.target !== target) {
      this.resizeObserver.disconnect()
      this.resizeObserver.observe(target)
      this.target = target
      this.didInitialPosition = false
      this.spotlight.classList.remove("demo-spotlight--dismissed")
      window.clearTimeout(this.dismissTimer)
      this.dismissTimer = window.setTimeout(() => {
        this.spotlight.classList.add("demo-spotlight--dismissed")
      }, 3600)
    }

    const message = this.element.querySelector(".demo-tour__prompt [data-demo-spotlight-message]")?.textContent.trim()
    this.callout.textContent = message || ""
    this.callout.hidden = !message
    this.arrow.hidden = !message

    const rect = target.getBoundingClientRect()
    if (rect.bottom < 0 || rect.top > window.innerHeight) {
      this.hide()
      if (!this.didInitialPosition) {
        this.didInitialPosition = true
        target.scrollIntoView({ block: "center", inline: "nearest" })
      }
      return
    }

    this.didInitialPosition = true
    this.spotlight.hidden = false
    this.spotlight.style.visibility = "visible"
    if (!message) return

    const width = window.innerWidth
    const height = window.innerHeight
    const calloutWidth = this.callout.offsetWidth
    const calloutHeight = this.callout.offsetHeight
    const placeBelow = height - rect.bottom >= calloutHeight + 52 || rect.top < calloutHeight + 52
    const calloutLeft = Math.max(16, Math.min(width - calloutWidth - 16, (rect.left + rect.right - calloutWidth) / 2))
    const calloutTop = placeBelow ? rect.bottom + 42 : rect.top - calloutHeight - 42
    Object.assign(this.callout.style, {
      left: `${calloutLeft}px`,
      top: `${Math.max(16, Math.min(height - calloutHeight - 16, calloutTop))}px`
    })
    this.arrow.textContent = placeBelow ? "↑" : "↓"
    Object.assign(this.arrow.style, {
      left: `${Math.max(8, Math.min(width - 48, (rect.left + rect.right) / 2 - 20))}px`,
      top: `${placeBelow ? rect.bottom + 5 : rect.top - 39}px`
    })
    this.arrow.hidden = placeBelow ? rect.bottom + 39 > height : rect.top < 39
  }
}
